
import {
    LunaCodeNode, ArgumentNode, ArithmeticNode, BooleanLiteral, BooleanLogicNode, 
    CaseNode,  DateLiteral, NumberLiteral, 
    FunctionNode,  IfNode, InNode, IsNullNode, 
    LikeNode, NotNode, NullLiteral, StringLiteral, VariableNode, WhenNode, NodeKind}
 from "../models/rules.model";

import * as jsonpath from "jsonpath/jsonpath";

import StaticPromise from "../models/static-promise";

export interface Arguments {
    context: any;
    args: Map<string, any>;
}


// FilterObject($.domains.wellTypesAvailable, @.validFor like '%foo%')
export interface FunctionDefinition {
    compileSync(compiler: ExpressionCompiler, funcNode: FunctionNode): (args: Arguments) => any ;
    compileAsync(compiler: AsyncExpressionCompiler, funcNode: FunctionNode): (args: Arguments) =>  Promise<any> ;
    name: string;


}

export abstract class SimpleFunctionDefinitionBase implements FunctionDefinition{

    abstract name:string;
    abstract invoke(a: Arguments, args: (arg: Arguments) => Array<any>): any;
    invokeAsync(a:Arguments, args: (arg: Arguments) => Promise<Array<any>>): Promise<any>{
        return StaticPromise.from(()=>{
            return args(a).then(funcParams=>{
                return this.invoke(a, (qq)=>funcParams);
            });
        });
    }
    protected compileArguments(compiler: ExpressionCompiler, argNodes: LunaCodeNode[]) : ((args:Arguments)=>any[]){
        const invocations = argNodes.map(n=>compiler.compileNode(n));
        const func = (args: Arguments): Array<any> =>  {
            return invocations.map((i) => i(args));
        };
        return func;        
    }
    compileSync(compiler: ExpressionCompiler, funcNode: FunctionNode): (args: Arguments) => any {
        const func = this.compileArguments(compiler, funcNode.parameterList);
        return (args: Arguments): any => { return this.invoke(args, func) };        
    }
    protected compileArgumentsAsync(compiler: AsyncExpressionCompiler, argNodes: LunaCodeNode[]) : ((args: Arguments) => Promise<any[]>){
        const invocations:((args: Arguments) => Promise<any>)[] = argNodes.map(n=>compiler.compileNode(n));
        const func = (args: Arguments): Promise<Array<any>> => {
            const promises =  invocations.map((i) => i(args));
            return Promise.all(promises);
        };
        return func;
    }
    compileAsync(compiler: AsyncExpressionCompiler, funcNode: FunctionNode): (args: Arguments) => Promise<any> {
        const argsFunc = this.compileArgumentsAsync(compiler, funcNode.parameterList);
        return (args: Arguments): Promise<any> => { return this.invokeAsync(args, argsFunc) };

    }    

}

class FunctionDefinitions
{
    find(name: string): FunctionDefinition {
        var candidates = this._funcs.filter(f=>f.name.toLowerCase() === name.toLowerCase());
        if(candidates.length > 0){
            return candidates[0];
        }
        return null;
    }
    public _funcs:FunctionDefinition[];
    get allFunctions (): FunctionDefinition[]{
        return this._funcs;
    };
    constructor (funcs:FunctionDefinition[]){
        this._funcs = funcs || [];
    }

}

export class AsyncExpressionCompiler{
    functionDefs: FunctionDefinitions;
    constructor(defs: Array<FunctionDefinition>) {
        this.functionDefs = new FunctionDefinitions(defs);

    }
    clone():AsyncExpressionCompiler{
        return new AsyncExpressionCompiler(this.functionDefs.allFunctions);
    }
    compile<TResult>(node: LunaCodeNode): (args: Arguments) => Promise<TResult> {
        return this.compileNode(node);;
    }
    compileNode(node: LunaCodeNode): (args: Arguments) => Promise<any> {
        let rv: (args: Arguments) => Promise<any>;
        switch (node.nodeType) {
            case NodeKind.ArgumentNode:
            {
                rv = this.compileArgumentNode(node as ArgumentNode);
                break;
            }
            case NodeKind.ArithmeticNode:
            {
                rv = this.compileArithmeticNode(node as ArithmeticNode);
                break;
            }
            case NodeKind.BooleanLiteral:
            {
                rv = this.compileBooleanLiteral(node as BooleanLiteral);
                break;
            }
            case NodeKind.BooleanLogicNode:
            {
                rv = this.compileBooleanLogicNode(node as BooleanLogicNode);
                break;
            }
            case NodeKind.CaseNode:
            {
                rv = this.compileCaseNode(node as CaseNode);
                break;
            }
            case NodeKind.DateLiteral:
            {
                rv = this.compileDateLiteral(node as DateLiteral);
                break;
            }
            case NodeKind.NumberLiteral:
            {
                rv = this.compileNumberLiteral(node as NumberLiteral);
                break;
            }
            case NodeKind.FunctionNode:
            {
                rv = this.compileFunctionNode(node as FunctionNode);
                break;
            }

            case NodeKind.IfNode:
            {
                rv = this.compileIfNode(node as IfNode);
                break;
            }
            case NodeKind.InNode:
            {
                rv = this.compileInNode(node as InNode);
                break;
            }
            case NodeKind.IsNullNode:
            {
                rv = this.compileIsNullNode(node as IsNullNode);
                break;
            }
            case NodeKind.LikeNode:
            {
                rv = this.compileLikeNode(node as LikeNode);
                break;
            }
            case NodeKind.NotNode:
            {
                rv = this.compileNotNode(node as NotNode);
                break;
            }
            case NodeKind.NullLiteral:
            {
                rv = this.compileNullLiteral(node as NullLiteral);
                break;
            }
            case NodeKind.StringLiteral:
            {
                rv = this.compileStringLiteral(node as StringLiteral);
                break;
            }
            case NodeKind.VariableNode:
            {
                rv = this.compileVariableNode(node as VariableNode);
                break;
            }
            case NodeKind.WhenNode:
            {
                rv = this.compileWhenNode(node as WhenNode);
                break;
            }
            default:
            {
                throw new Error(`Unknown Node ${node.nodeType}`);
            }
        }
        return rv;
    }
    _promiseFromValue(value:any) : Promise<any>{
        return new Promise<any>((resolve,reject)=>{resolve(value);});
    }
    compileArgumentNode(node: ArgumentNode): (args: Arguments) => Promise<any> {

        let nodeName = node.name;
        let path = null;
        const dotIndex = nodeName.indexOf(".");
        if(dotIndex > -1){
            nodeName = node.name.substr(0,dotIndex);
            path = "$"+node.name.substr(dotIndex);
        }
        return (a): any => {
            if (!(a.args.has(nodeName))) {
                throw new Error(`Missing parameter ${nodeName}`);
            }
            const argValue = a.args.get(nodeName);
            if(path!== null){
                const token = jsonpath.query(argValue, path);
                if (token.length === 0) {
                    throw new Error(`Path ${path} not found in context ${node.name}`);
                }
                return this._promiseFromValue(token[0]); 
            }
            return this._promiseFromValue(argValue);
        }

    }

    _getArithmeticOperation(operation:string) :(a:any,b:any)=>any{
        switch(operation){
            case "+": return (a,b)=>a+b; 
            case "-": return (a,b)=>a-b; 
            case "*": return (a,b)=>a*b; 
            case "/": return (a,b)=>a/b; 
            case "%": return (a, b) => a % b;
            case "&": return (a, b) => a & b;
            case "|": return (a, b) => a | b; 
            default: throw new Error(`Unknown operation ${operation}`);
        }
    }
    compileArithmeticNode(node: ArithmeticNode): (args: Arguments) => Promise<any> {
        const operation = node.operation;
        const lhs = this.compileNode(node.lhs);
        const rhs = this.compileNode(node.rhs);
        let op = this._getArithmeticOperation(operation);
        return (a)=>{
            let left = lhs(a).then(v=>({side:"L", value:v }));
            let right = rhs(a).then(v=>({side:"R", value:v}));
            return Promise.all([left,right]).then(values=>{
                let lv = values.find(v=>v.side === "L").value;
                let rv = values.find(v=>v.side === "R").value;
                return op(lv,rv);
            });            
        }
    }

    compileBooleanLiteral(node: BooleanLiteral): (args: Arguments) => Promise<any> {
        
        return (a: Arguments) => { return this._promiseFromValue(node.value); };
    }
    _getLogicOperation(op:string) : (a:any,b:any) =>boolean{
        if (op === "==" || op === "=") return (a,b)=> a === b;

        if (op === "!=" || op === "<>") return (a,b) => a!==b;

        if (op === "&&" || op === "and") return (a,b)=> a&&b;

        if (op === "||" || op === "or") return (a,b)=> a||b;

        if (op === ">") return (a,b)=> a>b;
        if (op === ">=") return (a,b)=> a>=b;
        if (op === "<") return (a,b)=> a<b;
        if (op === "<=") return (a,b)=> a<=b;
        throw new Error(`unknown comparison ${op}`);
    }
    compileBooleanLogicNode(node: BooleanLogicNode): (args: Arguments) => Promise<any> {
        const lhs = this.compileNode(node.lhs);
        const rhs = this.compileNode(node.rhs);

        let op = this._getLogicOperation(node.operation.toLowerCase());
        return (a)=>{

            let left = lhs(a).then(v=>({side:"L", value:v }));
            let right = rhs(a).then(v=>({side:"R", value:v}));
            return Promise.all([left,right]).then(values=>{
                let lv = values.find(v=>v.side === "L").value;
                let rv = values.find(v=>v.side === "R").value;
                return op(lv,rv) as boolean;
            });            
        };
        
    }
    compileLikeNode(node: LikeNode): (args: Arguments) => Promise<any> {
        const lhs = this.compileNode(node.lhs);
        const rhs = this.compileNode(node.rhs);
        let op = (leftValue,rightValue)=>{
            const pattern = this._transformLikeToRegex(rightValue);
            const regex = new RegExp(pattern);
            return regex.test(leftValue);
        }
        return (a)=>{

            let left = lhs(a).then(v=>({side:"L", value:v }));
            let right = rhs(a).then(v=>({side:"R", value:v}));
            return Promise.all([left,right]).then(values=>{
                let lv = values.find(v=>v.side === "L").value;
                let rv = values.find(v=>v.side === "R").value;
                return op(lv,rv) as boolean;
            });            
        };
    }
    compileCaseNode(node: CaseNode): (args: Arguments) => Promise<any> {
        const defaultValue = this.compileNode(node.defaultValue);
        let conditions: {condition:(args: Arguments) => Promise<any>, result:(args: Arguments) => Promise<any>}[]
            = node.conditions.map(cond=>({
                    condition:this.compileNode(cond.condition),
                    result: this.compileNode(cond.result)

            }));
        return (args:Arguments)=>{
            let promises:Promise<{index:number, evaluation:boolean, value:(args: Arguments) => Promise<any>}>[] = 
            conditions.map((c,i)=>c.condition(args).then(v=>{
                return{index:i, evaluation:v, value:c.result}
            }));
            let aggregation = Promise.all(promises)
            .then(values=>{
                values.sort(v => v.index);
                const result = values.find(v => v.evaluation);
                    return (result !== undefined && result !== null)
                        ? result.value(args)
                        : defaultValue(args);
            });
            return aggregation;
        }

    }

    compileDateLiteral(node: DateLiteral): (args: Arguments) => Promise<any> {
        return (args: Arguments): any => this._promiseFromValue( new Date(node.value));
    }

    compileNumberLiteral(node: NumberLiteral): (args: Arguments) => Promise<any> {
        return (args: Arguments): any => this._promiseFromValue( node.value as number);
    }

    compileFunctionNode(node: FunctionNode): (args: Arguments) => Promise<any> {
        const fd = this.functionDefs.find(node.name);
        if(!fd){
            throw new Error(`Invalid function name ${node.name}`);
        }
        return fd.compileAsync(this, node);
    }


    compileIfNode(node: IfNode): (args: Arguments) => Promise<any> {
        const condition = this.compileNode(node.condition);
        const trueResult = this.compileNode(node.trueResult);
        const falseResult = this.compileNode(node.falseResult);
        return (args: Arguments): Promise<any> => {
            return condition(args).then(v=>v? trueResult(args) : falseResult(args));
        };
    }

    compileInNode(node: InNode): (args: Arguments) => Promise<any> {
        const search = this.compileNode(node.expression);
        const valuesFuncs = node.parameterList.map(c=>this.compileNode(c));
        return (args: Arguments): Promise<any> => {
            return search(args).then(valueToFind=>{
                let promises :Promise<any>[]= valuesFuncs.map(f=>f(args));
                return Promise.all(promises)
                    .then(realValues=>realValues.find((v)=>v === valueToFind));
            });
        };
    }

    compileIsNullNode(node: IsNullNode): (args: Arguments) => Promise<any> {
        const inner = this.compileNode(node.lhs);
        return (args: Arguments): Promise<any> => inner(args).then(v=>v===null);
    }

    compileNotNode(node: NotNode): (args: Arguments) => Promise<any> {
        const inner = this.compileNode(node.child);
        return (args:Arguments)=>inner(args).then(v=>!(v as boolean));
    }

    compileNullLiteral(node: NullLiteral): (args: Arguments) => Promise<any> {
        return (args: Arguments): any => this._promiseFromValue( null);
    }

    compileStringLiteral(node: StringLiteral): (args: Arguments) => Promise<any> {
        return (args: Arguments): any => this._promiseFromValue(node.value as string);
    }

    compileVariableNode(node: VariableNode): (args: Arguments) => Promise<any> {
        return (args: Arguments): any => {
            const path = node.name;
            const token = jsonpath.query(args.context, path);
            if (token.length === 0) {
                console.error(`Path not found in context ${node.name}`);
                return this._promiseFromValue("");
            }
            return this._promiseFromValue(token[0]); //should there be an expectation that there is only one result??
        }
    }

    compileWhenNode(node: WhenNode): (args: Arguments) => Promise<any> {
        const condition = this.compileNode(node.condition);
        return (args: Arguments) => {
            return condition(args).then(v=>v as boolean);
        };
    }

    _transformLikeToRegex(pattern: string): string {
        pattern = pattern.replace("\\\\", "\\");
        pattern = pattern.replace("%", ".*?").replace("_", ".");
        pattern = pattern.replace("\\[", "[").replace("\\]", "]").replace("\\^", "^");
        return pattern;
    }
}

export class ExpressionCompiler {
    public functionDefs: FunctionDefinitions;
    constructor(defs: Array<FunctionDefinition>) {
        this.functionDefs = new FunctionDefinitions(defs);

    }
    clone():ExpressionCompiler{
        return new ExpressionCompiler(this.functionDefs.allFunctions);
    }

    compile<TResult>(node: LunaCodeNode): (args: Arguments) => TResult {
        const compiledNode = this.compileNode(node);
        return (a) => compiledNode(a) as TResult;
    }

    compileNode(node: LunaCodeNode): (args: Arguments) => any {
        let rv: any;
        switch (node.nodeType) {
            case NodeKind.ArgumentNode:
            {
                rv = this.compileArgumentNode(node as ArgumentNode);
                break;
            }
            case NodeKind.ArithmeticNode:
            {
                rv = this.compileArithmeticNode(node as ArithmeticNode);
                break;
            }
            case NodeKind.BooleanLiteral:
            {
                rv = this.compileBooleanLiteral(node as BooleanLiteral);
                break;
            }
            case NodeKind.BooleanLogicNode:
            {
                rv = this.compileBooleanLogicNode(node as BooleanLogicNode);
                break;
            }
            case NodeKind.CaseNode:
            {
                rv = this.compileCaseNode(node as CaseNode);
                break;
            }
            case NodeKind.DateLiteral:
            {
                rv = this.compileDateLiteral(node as DateLiteral);
                break;
            }
            case NodeKind.NumberLiteral:
            {
                rv = this.compileNumberLiteral(node as NumberLiteral);
                break;
            }
            case NodeKind.FunctionNode:
            {
                rv = this.compileFunctionNode(node as FunctionNode);
                break;
            }
            case NodeKind.IfNode:
            {
                rv = this.compileIfNode(node as IfNode);
                break;
            }
            case NodeKind.InNode:
            {
                rv = this.compileInNode(node as InNode);
                break;
            }
            case NodeKind.IsNullNode:
            {
                rv = this.compileIsNullNode(node as IsNullNode);
                break;
            }
            case NodeKind.LikeNode:
            {
                rv = this.compileLikeNode(node as LikeNode);
                break;
            }
            case NodeKind.NotNode:
            {
                rv = this.compileNotNode(node as NotNode);
                break;
            }
            case NodeKind.NullLiteral:
            {
                rv = this.compileNullLiteral(node as NullLiteral);
                break;
            }
            case NodeKind.StringLiteral:
            {
                rv = this.compileStringLiteral(node as StringLiteral);
                break;
            }
            case NodeKind.VariableNode:
            {
                rv = this.compileVariableNode(node as VariableNode);
                break;
            }
            case NodeKind.WhenNode:
            {
                rv = this.compileWhenNode(node as WhenNode);
                break;
            }
            default:
            {
                throw new Error(`Unknown Node ${node.nodeType}`);
            }
        }
        return rv;
    }

    compileArgumentNode(node: ArgumentNode): (args: Arguments) => any {
        let nodeName = node.name;
        let path = null;
        const dotIndex = nodeName.indexOf(".");
        if(dotIndex > -1){
            nodeName = node.name.substr(0,dotIndex);
            path = "$"+node.name.substr(dotIndex);

        }
        return (a): any => {
            if (!(a.args.has(nodeName))) {
                throw new Error(`Missing parameter ${nodeName}`);
            }
            const argValue = a.args.get(nodeName);
            if(path!== null){
                const token = jsonpath.query(argValue, path);
                if (token.length === 0) {
                    throw new Error(`Path ${path} not found in context ${node.name}`);
                }
                return token[0]; 
            }
            return argValue;
        }



    }

    compileArithmeticNode(node: ArithmeticNode): (args: Arguments) => number {
        const operation = node.operation;
        const lhs = this.compileNode(node.lhs);
        const rhs = this.compileNode(node.rhs);
        let rv: any;
        switch (operation) {
            case "+":
            {
                rv = (args: Arguments): number => {
                    return lhs(args) + rhs(args);
                };
                break;
            }
            case "-":
            {
                rv = (args: Arguments): number => {
                    return lhs(args) - rhs(args);
                    };
                break;
            }
            case "*":
            {
                rv = (args: Arguments): number => {
                    return lhs(args) * rhs(args);
                    };
                break;
            }
            case "/":
            {
                rv = (args: Arguments): number => {
                    return lhs(args) / rhs(args);
                    };
                break;
            }
            case "%":
            {
                rv = (args: Arguments): number => {
                    return lhs(args) % rhs(args);
                    };
                break;
            }
            case "&":
            {
                rv = (args: Arguments): number => {
                    return lhs(args) & rhs(args);
                };
                break;
            }
            case "|":
            {
                rv = (args: Arguments): number => {
                    return lhs(args) | rhs(args);
                };
                break;
            }
            default:
            {
                throw new Error(`Unknown operation ${operation}`);
            }
        }
        return rv;
    }

    compileBooleanLiteral(node: BooleanLiteral): (args: Arguments) => any {
        return (a: Arguments) => { return node.value as boolean; };
    }

    compileBooleanLogicNode(node: BooleanLogicNode): (args: Arguments) => boolean {
        const lhs = this.compileNode(node.lhs);
        const rhs = this.compileNode(node.rhs);
        const left = (args: Arguments) => lhs(args) as boolean;
        const right = (args: Arguments) => rhs(args) as boolean;
        const op = node.operation;

        if (op === "==" || op === "=") {
            return (args: Arguments): boolean => { return left(args) === right(args); };
        }
        if (op === "!=" || op === "<>") {
            return (args: Arguments): boolean => { return left(args) !== right(args); };
        }
        if (op === "&&" || op.toLowerCase() === "and") {
            return (args: Arguments): boolean => { return left(args) && right(args); };
        }
        if (op === "||" || op.toLowerCase() === "or") {
            return (args: Arguments): boolean => { return left(args) || right(args); };
        }

        if (op === ">") {
            return (args: Arguments): boolean => {
                const leftResult = lhs(args) as number;
                const rightResult = rhs(args) as number;
                return leftResult > rightResult;
            };
        }
        if (op === ">=") {
            return (args: Arguments): boolean => {
                const leftResult = lhs(args) as number;
                const rightResult = rhs(args) as number;
                return leftResult >= rightResult;
            };
        }
        if (op === "<") {
            return (args: Arguments): boolean => {
                const leftResult = lhs(args) as number;
                const rightResult = rhs(args) as number;
                return leftResult < rightResult;
            };
        }
        if (op === "<=") {
            return (args: Arguments): boolean => {
                const leftResult = lhs(args) as number;
                const rightResult = rhs(args) as number;
                return leftResult <= rightResult;
            };
        }
        throw new Error(`unknown comparison ${op}`);
    }

    compileCaseNode(node: CaseNode): (args: Arguments) => any {
        const defaultValue = this.compileNode(node.defaultValue);
        const conditions = new Array<any>();
        node.conditions.forEach((cond) => {
            const condition = this.compileNode(cond.condition);
            const result = this.compileNode(cond.result);
            conditions.push({ condition: condition, result: result });
        });
        return (args: Arguments): any => {
            for (let i = 0; i < conditions.length; i++) {
                if (conditions[i].condition(args) as boolean) {
                    return conditions[i].result(args);
                }
            }
            return defaultValue(args);
        };
    }

    compileDateLiteral(node: DateLiteral): (args: Arguments) => Date {
        return (args: Arguments): any => { return new Date(node.value); };
    }

    compileNumberLiteral(node: NumberLiteral): (args: Arguments) => number {
        return (args: Arguments): any => { return node.value as number; };
    }

    compileFunctionNode(node: FunctionNode): (args: Arguments) => any {
        let fd:FunctionDefinition = this.functionDefs.find(node.name);

        if (!fd) {
            throw new Error(`Invalid function name ${node.name}`);
        }
        return fd.compileSync(this, node);
    }



    compileIfNode(node: IfNode): (args: Arguments) => any {
        const condition = this.compileNode(node.condition);
        const trueResult = this.compileNode(node.trueResult);
        const falseResult = this.compileNode(node.falseResult);
        return (args: Arguments): any => {
            return (condition(args) as boolean) ? trueResult(args) : falseResult(args);
        };
    }

    compileInNode(node: InNode): (args: Arguments) => boolean {
        const search = this.compileNode(node.expression);
        const valueList = new Array<any>();
        node.parameterList.forEach((ch) => {
            valueList.push(this.compileNode(ch));
        });
        return (args: Arguments): boolean => {
            const resultList = new Array<any>();
            for (let i = 0; i < valueList.length; i++) {
                resultList.push(valueList[i](args));
            }
            return resultList.indexOf(search(args)) !== -1;
        };
    }

    compileIsNullNode(node: IsNullNode): (args: Arguments) => boolean {
        const inner = this.compileNode(node.lhs);
        return (args: Arguments): boolean => {
            return (inner(args) === null);
        };
    }

    compileLikeNode(node: LikeNode): (args: Arguments) => boolean {
        const lhs = this.compileNode(node.lhs);
        const lhsString = node.lhs.toString();
        const rhs = this.compileNode(node.rhs);
        const rhsString = node.rhs.toString();
        return (args: Arguments): boolean => {
            let leftValue: string;
            let rightValue: string;
            try {
                leftValue = lhs(args) as string;
            } catch (e) {
                throw new Error(`Cant cast left side, ${lhsString} to a string ${e}`);
            }
            try {
                rightValue = rhs(args) as string;
            } catch (e) {
                throw new Error(`Cant cast right side, ${rhsString} to a string ${e}`);
            }
            const pattern = this.transformLikeToRegex(rightValue);
            const regex = new RegExp(pattern);
            return regex.test(leftValue);
        }
    }

    compileNotNode(node: NotNode): (args: Arguments) => boolean {
        const inner = this.compileNode(node.child);
        const cast = (args: Arguments): boolean => {
            return !(inner(args) as boolean);
        };
        return (args: Arguments): boolean => {
            return cast(args);
        };
    }

    compileNullLiteral(node: NullLiteral): (args: Arguments) => any {
        return (args: Arguments): any => { return node.value; };
    }

    compileStringLiteral(node: StringLiteral): (args: Arguments) => string {
        return (args: Arguments): string => { return node.value as string; };
    }

    compileVariableNode(node: VariableNode): (args: Arguments) => any {
        return (args: Arguments): any => {            
            const path = node.name;
            const token = jsonpath.query(args.context, path);
            if (token.length === 0) {
                //console.error(`Path not found in context ${node.name}`);
                return "";
            }
            return token[0]; 
        }
    }

    compileWhenNode(node: WhenNode): (args: Arguments) => boolean {
        const condition = this.compileNode(node.condition);
        return (args: Arguments): boolean => {
            return condition(args) as boolean;
        };
    }

    transformLikeToRegex(pattern: string): string {
        pattern = pattern.replace("\\\\", "\\");
        pattern = pattern.replace("%", ".*?").replace("_", ".");
        pattern = pattern.replace("\\[", "[").replace("\\]", "]").replace("\\^", "^");
        return pattern;
    }
}